import Tokens from '@zapier/design-tokens';

const BaseColors = {
  // V5 Brand Colors
  zapier: '#ff4f00',
  earth: '#201515',
  moss: '#1f3121',
  night: '#2b2358',
  lavender: '#c1b7ff',
  peach: '#ffbf6e',
  bolt: '#f6ffdb',
  sky: '#cde4e1',
  cream: '#fff3e6',

  // Blue Jeans (blues)
  /** @deprecated Use blue-600 */
  blue: '#3d4592',
  /** @deprecated Use blue-500 */
  blue80: '#5e71c7',
  /** @deprecated Use blue-400 */
  blue60: '#90a1ed',
  /** @deprecated Use blue-300 */
  blue40: '#b2bce9',
  /** @deprecated Use blue-200 */
  blue20: '#d2d8f3',
  /** @deprecated Use blue-100 */
  blue10: '#f0f1fa',

  blue600: '#3d4592',
  blue500: '#5e71c7',
  blue400: '#90a1ed',
  blue300: '#b2bce9',
  blue200: '#d2d8f3',
  blue100: '#f0f1fa',

  // Error
  /** @deprecated Use error100-500 */
  error: '#e02d00',
  /** @deprecated Use error100-500 */
  error10: '#ffe1d5',
  /** @deprecated Use error100-500 */
  error120: '#b92602',

  error500: '#b83519',
  error400: '#ce482d',
  error300: '#e87057',
  error200: '#feb7a8',
  error100: '#fadfd9',

  // Warning
  /** @deprecated Use warning100-500 */
  warning: '#ffc43e',
  /** @deprecated Use warning100-500 */
  warning10: '#ffe7b2',
  warning500: '#eec913',
  warning400: '#ffd300',
  warning300: '#f8df68',
  warning200: '#fcea93',
  warning100: '#fff4bc',

  // Success
  /** @deprecated Use success100-500 */
  success: '#62d493',
  /** @deprecated Use success100-500 */
  success10: '#daf5e6',
  success500: '#086f3d',
  success400: '#0f884e',
  success300: '#36a770',
  success200: '#ade2c8',
  success100: '#def3e9',

  // Neutrals
  neutral800: '#2d2e2e',
  neutral700: '#403f3e',
  neutral600: '#95928e',
  neutral500: '#a8a5a0',
  neutral400: '#d7d5d2',
  neutral300: '#e8e7e4',
  neutral200: '#f7f5f2',
  neutral100: '#fffdf9',
  neutral100rgb: '255, 253, 249',

  // Special colors
  black: '#000000',
  white: '#ffffff',
  transparent: 'transparent',
  inherit: 'inherit',
  currentColor: 'currentColor',

  // Accent colors
  pink: '#fad9ef',
  purple: '#ebd9fa',

  /**
   * Deprecated colors. V5 CSS Custom Property names are commented on each color.
   **/
  // Lava (oranges / brand)
  /** @deprecated */
  lava: '#ff4a00',
  /** @deprecated */
  lava120: '#d93f00',
  /** @deprecated */
  lava80: '#ff6e33',
  /** @deprecated */
  lava60: '#ff9266',
  /** @deprecated */
  lava40: '#ffb799',
  /** @deprecated */
  lava20: '#ffdbcc',
  /** @deprecated */
  lava10: '#ffede6',
  /** @deprecated */
  lava5: '#fff6f2',

  // Ocean (blues)
  /** @deprecated */
  ocean: '#136bf5',
  /** @deprecated */
  ocean120: '#2655bd',
  /** @deprecated */
  ocean80: '#4289f7',
  /** @deprecated */
  ocean60: '#71a6f9',
  /** @deprecated */
  ocean40: '#a1c4fb',
  /** @deprecated */
  ocean20: '#d0e1fd',
  /** @deprecated */
  ocean10: '#e7f0fe',
  /** @deprecated */
  ocean5: '#f3f8ff',

  // Sun (yellows)
  /** @deprecated */
  sun: '#ffc43e',
  /** @deprecated */
  sun120: '#e6b038',
  /** @deprecated */
  sun80: '#ffd065',
  /** @deprecated */
  sun60: '#ffdc8b',
  /** @deprecated */
  sun40: '#ffe7b2',
  /** @deprecated */
  sun20: '#fff3d8',
  /** @deprecated */
  sun10: '#fff9ec',
  /** @deprecated */
  sun5: '#fffcf5',

  // Grays
  /** @deprecated */
  gray: '#333333',
  /** @deprecated */
  gray80: '#424242',
  /** @deprecated */
  gray60: '#757575',
  /** @deprecated */
  gray40: '#cccccc',
  /** @deprecated */
  gray20: '#e6e6e6',
  /** @deprecated */
  gray10: '#f2f2f2',
  /** @deprecated */
  gray5: '#fafafa',
} as const;
/**
 *
 **/

/** Alias common color assignments for consistency in implementations */
const ColorAliases = {
  /** @deprecated */
  brand: BaseColors.lava,
  blueJeans: BaseColors.blue600,
  bluebell: BaseColors.blue500,
  periwinkle: BaseColors.blue400,
  lilac: BaseColors.blue300,
  hydrangea: BaseColors.blue200,
  acidWash: BaseColors.blue100,
};

const PDLColors = {
  GrayWarm1: Tokens.GrayWarm1,
  GrayWarm2: Tokens.GrayWarm2,
  GrayWarm3: Tokens.GrayWarm3,
  GrayWarm4: Tokens.GrayWarm4,
  GrayWarm5: Tokens.GrayWarm5,
  GrayWarm6: Tokens.GrayWarm6,
  GrayWarm7: Tokens.GrayWarm7,
  GrayWarm8: Tokens.GrayWarm8,
  GrayWarm9: Tokens.GrayWarm9,
  GrayWarm10: Tokens.GrayWarm10,
  BrandOrange: Tokens.BrandOrange,
  BrandEarth: Tokens.BrandEarth,
  BrandLavender: Tokens.BrandLavender,
  BrandNight: Tokens.BrandNight,
  BrandAlmostWhite: Tokens.BrandAlmostWhite,
  BrandPeach: Tokens.BrandPeach,
  BrandCream: Tokens.BrandCream,
  BrandBolt: Tokens.BrandBolt,
  BrandSky: Tokens.BrandSky,
  BrandMoss: Tokens.BrandMoss,
  Purple1: Tokens.ColorPurple1,
  Purple2: Tokens.ColorPurple2,
  Purple3: Tokens.ColorPurple3,
  Purple4: Tokens.ColorPurple4,
  Purple5: Tokens.ColorPurple5,
  Yellow1: Tokens.ColorYellow1,
  Yellow2: Tokens.ColorYellow2,
  Yellow3: Tokens.ColorYellow3,
  Yellow4: Tokens.ColorYellow4,
  Yellow5: Tokens.ColorYellow5,
  Red1: Tokens.ColorRed1,
  Red2: Tokens.ColorRed2,
  Red3: Tokens.ColorRed3,
  Red4: Tokens.ColorRed4,
  Red5: Tokens.ColorRed5,
  Blue1: Tokens.ColorBlue1,
  Blue2: Tokens.ColorBlue2,
  Blue3: Tokens.ColorBlue3,
  Blue4: Tokens.ColorBlue4,
  Blue5: Tokens.ColorBlue5,
  Orange1: Tokens.ColorOrange1,
  Orange2: Tokens.ColorOrange2,
  Orange3: Tokens.ColorOrange3,
  Orange4: Tokens.ColorOrange4,
  Orange5: Tokens.ColorOrange5,
  Teal1: Tokens.ColorTeal1,
  Teal2: Tokens.ColorTeal2,
  Teal3: Tokens.ColorTeal3,
  Teal4: Tokens.ColorTeal4,
  Teal5: Tokens.ColorTeal5,
  Green1: Tokens.ColorGreen1,
  Green2: Tokens.ColorGreen2,
  Green3: Tokens.ColorGreen3,
  Green4: Tokens.ColorGreen4,
  Green5: Tokens.ColorGreen5,
  Pink1: Tokens.ColorPink1,
  Pink2: Tokens.ColorPink2,
  Pink3: Tokens.ColorPink3,
  Pink4: Tokens.ColorPink4,
  Pink5: Tokens.ColorPink5,
  PrimeWhite: Tokens.PrimeWhite,
  PrimeBlack: Tokens.PrimeBlack,
  PrimePurple: Tokens.PrimePurple,
  PrimeBlue: Tokens.PrimeBlue,
  PrimeTeal: Tokens.PrimeTeal,
  PrimeGreen: Tokens.PrimeGreen,
  PrimeYellow: Tokens.PrimeYellow,
  PrimeOrange: Tokens.PrimeOrange,
  PrimeRed: Tokens.PrimeRed,
  PrimePink: Tokens.PrimePink,
  UiBrandWeakest: Tokens.UiBrandWeakest,
  UiBrandWeaker: Tokens.UiBrandWeaker,
  UiBrand: Tokens.UiBrand,
  UiBrandStronger: Tokens.UiBrandStrongest,
  UiBrandStrongest: Tokens.UiBrandStrongest,
  UiPrimaryWeakest: Tokens.UiPrimaryWeakest,
  UiPrimaryWeaker: Tokens.UiPrimaryWeaker,
  UiPrimary: Tokens.UiPrimary,
  UiPrimaryStronger: Tokens.UiPrimaryStronger,
  UiPrimaryStrongest: Tokens.UiPrimaryStrongest,
  StatusWarningWeakest: Tokens.StatusWarningWeakest,
  StatusWarningWeaker: Tokens.StatusWarningWeaker,
  StatusWarning: Tokens.StatusWarning,
  StatusWarningStronger: Tokens.StatusWarningStronger,
  StatusWarningStrongest: Tokens.StatusWarningStrongest,
  StatusErrorWeakest: Tokens.StatusErrorWeakest,
  StatusErrorWeaker: Tokens.StatusErrorWeaker,
  StatusError: Tokens.StatusError,
  StatusErrorStronger: Tokens.StatusErrorStronger,
  StatusErrorStrongest: Tokens.StatusErrorStrongest,
  StatusSuccessWeakest: Tokens.StatusSuccessWeakest,
  StatusSuccessWeaker: Tokens.StatusSuccessWeaker,
  StatusSuccess: Tokens.StatusSuccess,
  StatusSuccessStronger: Tokens.StatusSuccessStronger,
  StatusSuccessStrongest: Tokens.StatusSuccessStrongest,
  BackgroundWeaker: Tokens.BackgroundWeaker,
  BackgroundDefault: Tokens.BackgroundDefault,
  BackgroundStronger: Tokens.BackgroundStronger,
  BackgroundStrongest: Tokens.BackgroundStrongest,
  BackgroundSelected: Tokens.BackgroundSelected,
  BackgroundOverlay: Tokens.BackgroundOverlay,
  StrokeWeaker: Tokens.StrokeWeaker,
  StrokeDefault: Tokens.StrokeDefault,
  StrokeStronger: Tokens.StrokeStronger,
  StrokeSelected: Tokens.StrokeSelected,
  ReservedBeta: Tokens.ReservedBeta,
  ReservedInvite: Tokens.ReservedInvite,
  ReservedFree: Tokens.ReservedFree,
  TextWeakest: Tokens.TextWeakest,
  TextWeaker: Tokens.TextWeaker,
  TextDefault: Tokens.TextDefault,
  TextStronger: Tokens.TextStronger,
  TextLink: Tokens.TextLink,
  TextLinkHover: Tokens.TextLinkHover,
  TextInverted: Tokens.TextInverted,
};

export const Colors = {
  ...BaseColors,
  ...ColorAliases,
  ...PDLColors,
};

export type ColorName = keyof typeof Colors;
export type ColorValue = (typeof Colors)[ColorName];
export type Neutrals =
  | 'neutral100'
  | 'neutral200'
  | 'neutral300'
  | 'neutral400'
  | 'neutral500'
  | 'neutral600'
  | 'neutral700'
  | 'neutral800';

export const ColorsCSS = {
  // Since we're only overriding the "new" colors,
  // let's preserve the deprecated color values in this object for safety.
  ...Colors,

  // Brand
  zapier: `var(--zds-brand-orange, #ff4f00)`,
  earth: `var(--zds-brand-earth, #201515)`,
  moss: `var(--zds-brand-moss, #1f3121)`,
  night: `var(--zds-brand-night, #2b2358)`,
  lavender: `var(--zds-brand-lavender, #c1b7ff)`,
  peach: `var(--zds-brand-peach, #ffbf6e)`,
  bolt: `var(--zds-brand-bolt, #f6ffdb)`,
  sky: `var(--zds-brand-sky, #cde4e1)`,
  cream: `var(--zds-brand-cream, #fff3e6)`,

  // Blue Jeans (blues)
  blue600: `var(--zds-ui-primary-strongest, #3d4592)`,
  blue500: `var(--zds-ui-primary-stronger, #5e71c7)`,
  blue400: `var(--zds-ui-primary, #90a1ed)`,
  blue300: `var(--zds-ui-primary-weaker, #b2bce9)`,
  blue200: `var(--zds-ui-primary-weaker, #d2d8f3)`,
  blue100: `var(--zds-ui-primary-weakest, #f0f1fa)`,

  // Error
  error500: `var(--zds-status-error-strongest, #b83519)`,
  error400: `var(--zds-status-error-stronger, #ce482d)`,
  error300: `var(--zds-status-error, #e87057)`,
  error200: `var(--zds-status-error-weaker, #feb7a8)`,
  error100: `var(--zds-status-error-weakest, #fadfd9)`,

  // Warning
  warning500: `var(--zds-status-warning-strongest, #eec913)`,
  warning400: `var(--zds-status-warning-stronger, #ffd300)`,
  warning300: `var(--zds-status-warning, #f8df68)`,
  warning200: `var(--zds-status-warning-weaker, #fcea93)`,
  warning100: `var(--zds-status-warning-weakest, #fff4bc)`,

  // Success
  success500: `var(--zds-status-success-strongest, #086f3d)`,
  success400: `var(--zds-status-success-stronger, #0f884e)`,
  success300: `var(--zds-status-success, #36a770)`,
  success200: `var(--zds-status-success-weaker, #ade2c8)`,
  success100: `var(--zds-status-success-weakest, #def3e9)`,

  // Neutrals
  neutral800: `var(--zds-gray-warm-10, #2d2e2e)`,
  neutral700: `var(--zds-gray-warm-9, #403f3e)`,
  neutral600: `var(--zds-gray-warm-6, #95928e)`,
  neutral500: `var(--zds-gray-warm-5, #a8a5a0)`,
  neutral400: `var(--zds-gray-warm-4, #d7d5d2)`,
  neutral300: `var(--zds-gray-warm-3, #e8e7e4)`,
  neutral200: `var(--zds-gray-warm-2, #f7f5f2)`,
  neutral100: `var(--zds-brand-almost-white, #fffdf9)`,

  // Accent colors
  pink: `var(--zds-color-pink-2, #fad9ef)`,
  purple: `var(--zds-color-purple-2, #ebd9fa)`,
};
